/**
 * Training Consumption Confirmation Dialog
 * Shown after a technician opens training content. Collects the user's reported
 * completion and — K108-R Batch 3 — shows OptiBay's OBSERVED monitored engagement
 * (from the extension evidence read model) with options tailored to that
 * percentage. Honest language only: OptiBay "observed" engagement evidence; the
 * user "reported"; NEVER "verified completion"; NEVER accusatory. A neutral
 * attestation snapshot (incl. any mismatch) is stored in the session metadata.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Eye } from 'lucide-react';
import { useConfirmConsumption, usePostponeReminder } from '@/hooks/useTrainingConsumption';
import { useUpdateSoftProficiency } from '@/hooks/useSoftProficiency';
import { useUpdateInferredStyles } from '@/hooks/useLearningProfile';
import { useAllConsumptionSessions } from '@/hooks/useTrainingConsumption';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';
import {
  buildTrainingAttestation,
  completionOptionsForBucket,
  mismatchNoteCopy,
  monitoredBucket,
  observedEngagementCopy,
  type CompletionOptionDef,
} from '@/lib/training/trainingCompletionOptions';
import { calculateInferredStyles } from '@/utils/divinationEngine';
import { toast } from 'sonner';

interface TrainingConsumptionConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: {
    id: string;
    artifact: {
      id: string;
      title: string;
      artifact_type: string;
      provider_name?: string;
    };
    technician_profile_id: string;
  };
  artifactDomains?: string[];
  // K108-Y: neutral title override for the resumed-partial "update your report" flow.
  titleOverride?: string;
  /**
   * Phase Q1 — fired ONLY after a successful "completed" attestation (not partial,
   * skipped, remind, or cancel). The provider uses it to offer the optional
   * post-completion knowledge check. Flag-gated downstream; a no-op when absent.
   */
  onCompleted?: (info: { artifactId: string; sessionId: string }) => void;
}

export function TrainingConsumptionConfirmDialog({
  open,
  onOpenChange,
  session,
  artifactDomains = [],
  titleOverride,
  onCompleted,
}: TrainingConsumptionConfirmDialogProps) {
  // K108-R Batch 2 read model — OptiBay's observed monitored engagement.
  const { data: summaries } = useTrainingSessionMonitoringSummary({
    sessionIds: [session.id],
    enabled: open,
  });
  const monitoring = summaries?.get(session.id) ?? null;
  const monitoredPercent = monitoring?.monitoredPercent ?? null;
  const observedMinutes = monitoring?.observedMinutes ?? null;
  const evidenceUnavailable = monitoring?.evidenceUnavailable ?? true;
  const confidence = monitoring?.confidence ?? null;

  const bucket = monitoredBucket(monitoredPercent);
  const options = useMemo(() => completionOptionsForBucket(bucket), [bucket]);
  const [selectedId, setSelectedId] = useState<string>(options[0].id);

  // Keep the default valid as the bucket resolves (evidence loads async).
  useEffect(() => {
    if (!options.some((o) => o.id === selectedId)) setSelectedId(options[0].id);
  }, [options, selectedId]);

  const confirmMutation = useConfirmConsumption();
  const postponeMutation = usePostponeReminder();
  const updateSoftProficiency = useUpdateSoftProficiency();
  const updateInferredStyles = useUpdateInferredStyles();
  const { data: allSessions, refetch: refetchSessions } = useAllConsumptionSessions(
    session.technician_profile_id,
  );

  const isSubmitting = confirmMutation.isPending || postponeMutation.isPending;

  const handleSubmit = async () => {
    const opt: CompletionOptionDef = options.find((o) => o.id === selectedId) ?? options[0];
    const nowIso = new Date().toISOString();
    const attestation = buildTrainingAttestation({
      userReported: opt.userReported,
      monitoredPercent,
      confidence,
      evidenceUnavailable,
      at: nowIso,
    });

    try {
      if (opt.action === 'remind') {
        await postponeMutation.mutateAsync({
          sessionId: session.id,
          technicianProfileId: session.technician_profile_id,
          postponeHours: 24,
        });
        toast.success('Reminder set for tomorrow');
        onOpenChange(false);
        return;
      }

      const status = opt.confirmStatus ?? 'partial';
      const result = await confirmMutation.mutateAsync({
        sessionId: session.id,
        technicianProfileId: session.technician_profile_id,
        artifactId: session.artifact.id,
        data: {
          status,
          progressPercent: opt.progressPercent,
          // Neutral attestation snapshot → session metadata (no schema change).
          attestation,
        },
      });

      // Soft proficiency only for reported completion/partial (never skipped).
      if (status !== 'skipped' && result.confidence !== 'suspicious') {
        for (const domain of artifactDomains) {
          await updateSoftProficiency.mutateAsync({
            technicianProfileId: session.technician_profile_id,
            categoryKey: domain,
            completionStatus: status as 'completed' | 'partial',
            progressPercent: opt.progressPercent,
            confidence: result.confidence,
          });
        }
      }

      await refetchSessions();
      if (allSessions) {
        const inference = calculateInferredStyles(allSessions);
        await updateInferredStyles.mutateAsync({
          technicianProfileId: session.technician_profile_id,
          inferredStyles: inference.inferredStyles,
          inferredConfidence: inference.inferredConfidence,
        });
      }

      // Neutral confirmation — surfaces the mismatch WITHOUT accusing.
      toast.success(attestation.mismatch ? mismatchNoteCopy(monitoredPercent) : 'Your report has been saved.');
      onOpenChange(false);
      // Phase Q1 — a full "completed" report may chain into the optional quiz.
      if (status === 'completed') {
        onCompleted?.({ artifactId: session.artifact.id, sessionId: session.id });
      }
    } catch (error) {
      console.error('Error confirming consumption:', error);
      toast.error('Failed to save. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titleOverride ?? 'Did you complete this training?'}</DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="font-medium text-foreground">{session.artifact.title}</span>
            {session.artifact.provider_name && (
              <span className="text-muted-foreground"> - {session.artifact.provider_name}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Observed engagement — honest, non-accusatory, not verified completion. */}
        <div
          data-testid="observed-engagement"
          className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
        >
          <Eye className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            {observedEngagementCopy(monitoredPercent, evidenceUnavailable, observedMinutes)}
            {confidence && !evidenceUnavailable ? (
              <span className="block text-xs">
                Engagement evidence: {confidence} (not verified completion).
              </span>
            ) : null}
          </span>
        </div>

        <div className="py-2">
          <RadioGroup value={selectedId} onValueChange={setSelectedId} className="space-y-3">
            {options.map((opt) => (
              <div
                key={opt.id}
                className="flex items-center space-x-3 rounded-lg border p-3 transition-colors hover:bg-accent/50"
              >
                <RadioGroupItem value={opt.id} id={opt.id} />
                <Label htmlFor={opt.id} className="flex-1 cursor-pointer">
                  {opt.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="confirm-submit">
            {isSubmitting ? 'Saving...' : 'Submit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
